import { type LucideIcon } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

// No font size or line height: a pill takes the text it sits in.
const BASE_PILL_CLASS =
    'focus-visible:outline-agent-action rounded-md px-1.5 py-px font-medium no-underline transition-colors duration-[120ms] outline-none focus-visible:outline-2 focus-visible:outline-offset-2';

// Atomic pills need a margin to separate lines without changing their tint.
export const REFERENCE_PILL_CLASS = `${BASE_PILL_CLASS} my-[1.5px] inline-flex max-w-full items-center align-baseline`;

// Prose pills break with the sentence and repeat their tint on every line.
export const WRAPPING_REFERENCE_PILL_CLASS = `${BASE_PILL_CLASS} box-decoration-clone`;

export function ReferencePillContent({
    Icon,
    label,
    wrap,
}: Readonly<{ Icon: LucideIcon; label: string; wrap: boolean }>): ReactNode {
    if (wrap) {
        const firstSpace = label.indexOf(' ');
        const leadWord = firstSpace === -1 ? label : label.slice(0, firstSpace);
        return (
            <Fragment>
                {/* Keep the icon with the first word at a line break. */}
                <span className="whitespace-nowrap">
                    <Icon
                        aria-hidden="true"
                        className="mr-1 inline size-[1em] align-[-0.15em] text-current"
                    />
                    {leadWord}
                </span>
                {label.slice(leadWord.length)}
            </Fragment>
        );
    }
    return (
        <Fragment>
            {/* An inline-flex box takes its baseline from its first flex
                item; an svg only offers its bottom edge, which floats the
                whole chip above surrounding text. This zero-width space is
                a real text baseline, so the chip sits on the line by its
                label like ordinary text. */}
            <span aria-hidden="true" className="w-0 select-none">
                {'\u200b'}
            </span>
            <Icon
                aria-hidden="true"
                // The icon follows the pill's text color, so every tint
                // (including the status-aware shift palette) stays one hue.
                className="size-[1em] shrink-0 text-current"
            />
            <span className="ml-1 min-w-0 truncate">{label}</span>
        </Fragment>
    );
}
